import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  FiRefreshCw,
  FiEdit,
  FiSave,
  FiX,
  FiImage,
  FiUploadCloud,
  FiPaperclip,
  FiDownload,
  FiChevronDown,
  FiChevronUp,
  FiCheck,
  FiEye,
  FiSearch,
  FiBookmark,
} from "react-icons/fi";
import CustomTextArea from "../../components/CustomTextArea";
import { ClipLoader } from "react-spinners";
import ImagePreviewModal from "./ImagePreviewModal";

/**
 * Generated post component
 * @param {Object} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const GeneratedPost = ({
  generatedPost,
  generatedPostRef,
  isEditMode,
  toggleEditMode,
  editedPostText,
  setEditedPostText,
  setHasEdited,
  isRegeneratingPost,
  isRegeneratingImage,
  regeneratePost,
  regenerateImages,
  serperImages,
  selectedSerperImages,
  setSelectedSerperImages,
  selectedImages,
  setSelectedImages,
  geminiGeneratedImage,
  imageSource,
  allowNoImage,
  isPublishing,
  publishPost,
  publishToProfile,
  selectedFanpages,
  fanpages,
  isDropdownOpen,
  toggleDropdown,
  setIsDropdownOpen,
  handleFanpageSelect,
  showFacebookPublishing = true,
  activeDropdown,
  setActiveDropdown,
  hasFanpages = true,
  navigateToProfile,
  generatedPosts = [],
  selectedPostIndex = 0,
  selectPost,
  isSaving,
  savePost,
  savedPostsCount = 0,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [isImagesExpanded, setIsImagesExpanded] = useState(true);
  const [localImages, setLocalImages] = useState([]);

  // Image preview modal state
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewImageIndex, setPreviewImageIndex] = useState(0);

  // Dropdown ref
  const dropdownRef = useRef(null);

  // Update isDropdownOpen based on activeDropdown
  useEffect(() => {
    // Nếu activeDropdown không phải là 'fanpage', đóng dropdown fanpage
    if (activeDropdown !== "fanpage") {
      setIsDropdownOpen && setIsDropdownOpen(false);
    }
  }, [activeDropdown, setIsDropdownOpen]);

  // Theo dõi sự thay đổi của selectedPostIndex để đảm bảo hình ảnh được giữ lại
  useEffect(() => {
    // Khi người dùng chuyển đổi giữa các phiên bản bài viết
    // Đảm bảo rằng generatedPost.images được cập nhật với localImages
    if (generatedPost && localImages && localImages.length > 0) {
      generatedPost.images = [...localImages];
    }
  }, [selectedPostIndex]);

  // Handle click outside for fanpage dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen && setIsDropdownOpen(false);
        if (activeDropdown === "fanpage") {
          setActiveDropdown && setActiveDropdown(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeDropdown, setActiveDropdown, setIsDropdownOpen]);

  // Đồng bộ generatedPost.images với localImages khi generatedPost thay đổi
  useEffect(() => {
    // Nếu đã có localImages (đã upload ảnh), giữ nguyên localImages
    // Điều này đảm bảo ảnh được giữ lại khi chuyển đổi giữa các phiên bản
    if (localImages && localImages.length > 0) {
      // Cập nhật generatedPost.images để đảm bảo tất cả phiên bản đều sử dụng cùng ảnh
      if (generatedPost) {
        generatedPost.images = [...localImages];
      }
    }
    // Nếu chưa có localImages nhưng generatedPost có images, sử dụng images từ generatedPost
    else if (generatedPost && generatedPost.images) {
      setLocalImages([...generatedPost.images]);
    } else {
      setLocalImages([]);
    }
  }, [generatedPost]);

  // Copy post text to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedPost.text);
    toast.success("Đã sao chép nội dung bài viết!", {
      position: "top-right",
      autoClose: 2000,
    });
  };

  // Handle Serper image selection
  const handleSelectImage = (imageUrl) => {
    // Toggle selection
    if (selectedSerperImages.includes(imageUrl)) {
      setSelectedSerperImages(
        selectedSerperImages.filter((url) => url !== imageUrl)
      );
    } else {
      setSelectedSerperImages([...selectedSerperImages, imageUrl]);
    }
  };

  // Handle image preview
  const handleImagePreview = (index) => {
    setPreviewImageIndex(index);
    setIsPreviewModalOpen(true);
  };

  // Close preview modal
  const closePreviewModal = () => {
    setIsPreviewModalOpen(false);
  };

  // Toggle expand/collapse
  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  // Toggle images section
  const toggleImages = () => {
    setIsImagesExpanded(!isImagesExpanded);
  };

  // Format post text with line breaks
  const formattedText = generatedPost?.text || "";

  // Handle text changes in edit mode
  const handleTextChange = (e) => {
    setEditedPostText(e.target.value);
    setHasEdited(true);
  };

  // Handle download post
  const handleDownload = () => {
    const element = document.createElement("a");
    const text = isEditMode ? editedPostText : formattedText;
    const file = new Blob([text], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = "bai-viet.txt";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // Kiểm tra và hiển thị thông báo nếu chưa kết nối fanpage
  const renderFanpageWarning = () => {
    if (!hasFanpages) {
      return (
        <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-700">
            Bạn chưa kết nối Fanpage Facebook. Kết nối Fanpage để đăng bài.
          </p>
          <button
            onClick={navigateToProfile}
            className="mt-2 text-sm bg-yellow-100 hover:bg-yellow-200 text-yellow-800 font-medium py-1 px-3 rounded transition-colors"
          >
            Kết nối Fanpage
          </button>
        </div>
      );
    }
    return null;
  };

  // Thêm hàm xử lý click tải ảnh chung cho cả Windows và macOS
  const handleImageClick = (inputId) => {
    const inputElement = document.getElementById(inputId);
    if (inputElement) {
      // Trên Windows, đôi khi click không hoạt động bình thường
      // nên chúng ta gọi click() một cách rõ ràng
      setTimeout(() => {
        inputElement.click();
      }, 0);
    }
  };

  // Thêm hàm xử lý xóa ảnh chung
  const handleDeleteImage = (index) => {
    // Tạo bản sao của mảng images
    const newImages = [...localImages];
    // Xóa ảnh tại vị trí index
    newImages.splice(index, 1);

    // Cập nhật state localImages
    setLocalImages(newImages);

    // Cập nhật lại generatedPost.images
    if (generatedPost) {
      generatedPost.images = [...newImages];
    }

    // Cập nhật selectedImages nếu hàm được cung cấp để đồng bộ giữa các component
    if (typeof setSelectedImages === "function") {
      setSelectedImages([...newImages]);
    }

    // Cập nhật hình ảnh cho tất cả các phiên bản bài viết
    if (generatedPosts && generatedPosts.length > 0) {
      generatedPosts.forEach((post) => {
        if (post) {
          post.images = [...newImages];
        }
      });
    }

    // Đánh dấu đã chỉnh sửa để force re-render
    setHasEdited(true);

    toast.success("Đã xóa ảnh thành công!", {
      position: "top-right",
      autoClose: 2000,
    });
  };

  // Thêm hàm xử lý file ảnh được chọn
  const processImageFiles = async (files, existingImages = []) => {
    try {
      const processedImages = [];
      for (const file of files) {
        // Import các hàm cần thiết từ utils
        const { compressImage, fileToBase64 } = await import(
          "../utils/imageUtils"
        );

        // Nén ảnh để giảm kích thước
        const compressedBlob = await compressImage(file, {
          maxWidth: 800,
          maxHeight: 800,
          quality: 0.7,
        });

        // Chuyển đổi blob thành base64
        const base64Image = await fileToBase64(compressedBlob);
        processedImages.push(base64Image);
      }

      // Cập nhật localImages - kết hợp với ảnh hiện có
      const newImages = [...existingImages, ...processedImages];
      setLocalImages(newImages);

      // Cập nhật generatedPost với ảnh mới
      if (generatedPost) {
        generatedPost.images = [...newImages];
      }

      // Cập nhật hình ảnh cho tất cả các phiên bản bài viết
      if (generatedPosts && generatedPosts.length > 0) {
        generatedPosts.forEach((post) => {
          if (post) {
            post.images = [...newImages];
          }
        });
      }

      // Cập nhật selectedImages để đảm bảo ảnh được đăng lên Facebook
      if (typeof setSelectedImages === "function") {
        setSelectedImages(newImages);
      }

      // Force re-render
      setHasEdited(true);

      return true;
    } catch (error) {
      console.error("Lỗi khi xử lý ảnh:", error);
      toast.error("Có lỗi xảy ra khi xử lý ảnh!", {
        position: "top-right",
        autoClose: 3000,
      });
      return false;
    }
  };

  return (
    <div
      ref={generatedPostRef}
      className="bg-white rounded-md shadow-md p-5 overflow-visible mb-20 md:mb-0"
    >
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-800">Bài viết đã tạo</h2>
        {!isEditMode && (
          <div className="flex gap-2">
            <button
              onClick={toggleEditMode}
              className="px-3 py-2.5 sm:py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-md font-medium text-sm flex items-center"
            >
              <FiEdit className="mr-1" />
              Sửa
            </button>
            <button
              onClick={copyToClipboard}
              className="px-3 py-2.5 sm:py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-md font-medium text-sm"
            >
              Sao chép
            </button>
            <div className="relative group">
              <button
                onClick={savePost}
                disabled={isSaving || savedPostsCount >= 10}
                className={`px-3 py-2.5 sm:py-1.5 ${
                  savedPostsCount >= 10
                    ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                    : "bg-blue-50 hover:bg-blue-100 text-blue-700"
                } rounded-md font-medium text-sm flex items-center`}
              >
                {isSaving ? (
                  <FiRefreshCw className="animate-spin mr-1" />
                ) : (
                  <FiBookmark className="mr-1" />
                )}
                {isSaving ? "Đang lưu..." : "Lưu"}
              </button>
              <div className="absolute hidden group-hover:block z-10 w-64 p-2 mt-2 text-sm bg-gray-800 text-white rounded-md shadow-lg right-0">
                Bạn đã lưu {savedPostsCount}/10 bài viết
                {savedPostsCount >= 10 && (
                  <div className="mt-1 text-red-300">
                    Đã đạt giới hạn tối đa. Vui lòng xóa bớt bài viết cũ.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {isEditMode && (
          <div className="flex gap-2">
            <button
              onClick={toggleEditMode}
              className="px-3 py-2.5 sm:py-1.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-md font-medium text-sm flex items-center"
            >
              <FiSave className="mr-1" />
              Lưu
            </button>
            <button
              onClick={() => {
                if (
                  window.confirm(
                    "Bạn có chắc chắn muốn hủy các thay đổi không?"
                  )
                ) {
                  setEditedPostText(generatedPost.text);
                  toggleEditMode();
                }
              }}
              className="px-3 py-2.5 sm:py-1.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-md font-medium text-sm flex items-center"
            >
              <FiX className="mr-1" />
              Hủy
            </button>
          </div>
        )}
      </div>

      {/* Tabs for multiple posts - Thêm UI cho việc chuyển đổi giữa các phiên bản */}
      {generatedPosts.length > 1 && (
        <div className="mb-4 border-b border-gray-200">
          <div className="flex overflow-x-auto">
            {generatedPosts.map((post, index) => (
              <button
                key={index}
                className={`py-2 px-4 font-medium text-sm whitespace-nowrap ${
                  index === selectedPostIndex
                    ? "border-b-2 border-blue-500 text-blue-600"
                    : "text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300"
                }`}
                onClick={() => selectPost(index)}
              >
                Phiên bản {index + 1}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Nội dung bài viết */}
      <div className="mb-6">
        {isEditMode ? (
          <CustomTextArea
            value={editedPostText}
            onChange={(e) => {
              setEditedPostText(e.target.value);
              setHasEdited(true);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            rows={10}
          />
        ) : (
          <div className="bg-gray-50 p-4 rounded-md whitespace-pre-wrap">
            {formattedText}
          </div>
        )}
      </div>

      {/* Hình ảnh */}
      {!allowNoImage && (
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-medium text-gray-700">Hình ảnh</h3>
          </div>

          {/* Hiển thị ảnh dựa trên nguồn */}
          {imageSource === "internet" && (
            <div>
              {isRegeneratingImage ? (
                <div className="flex justify-center items-center h-40 bg-gray-100 rounded-md">
                  <ClipLoader color="#3B82F6" size={40} />
                </div>
              ) : serperImages.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {serperImages.map((image, index) => (
                    <div
                      key={index}
                      className={`relative rounded-md overflow-hidden border-2 cursor-pointer group ${
                        selectedSerperImages.includes(image.imageUrl)
                          ? "border-blue-500"
                          : "border-transparent"
                      }`}
                      onClick={() => handleSelectImage(image.imageUrl)}
                    >
                      <img
                        src={image.imageUrl}
                        alt={`Ảnh ${index + 1}`}
                        className="w-full h-32 object-cover transition-transform group-hover:scale-105"
                      />

                      {/* Preview Eye Icon */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleImagePreview(index);
                        }}
                        className="absolute top-1 right-1 bg-black bg-opacity-60 text-white p-1.5 rounded-full hover:bg-opacity-80 transition-all duration-200 shadow-sm"
                        title="Xem preview ảnh"
                      >
                        <FiEye size={14} />
                      </button>

                      {/* Selection Checkmark */}
                      {selectedSerperImages.includes(image.imageUrl) && (
                        <div className="absolute top-1 left-1 bg-blue-500 text-white rounded-full p-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex justify-center items-center h-40 bg-gray-100 rounded-md">
                  <div className="text-center text-gray-500">
                    <FiImage className="mx-auto h-10 w-10 mb-2" />
                    <p>Không có ảnh</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {imageSource === "ai" && (
            <div>
              {isRegeneratingImage ? (
                <div className="flex justify-center items-center h-40 bg-gray-100 rounded-md">
                  <ClipLoader color="#3B82F6" size={40} />
                </div>
              ) : geminiGeneratedImage ? (
                <div className="rounded-md overflow-hidden">
                  <img
                    src={geminiGeneratedImage}
                    alt="Ảnh được tạo bởi AI"
                    className="w-full h-auto"
                  />
                </div>
              ) : (
                <div className="flex justify-center items-center h-40 bg-gray-100 rounded-md">
                  <div className="text-center text-gray-500">
                    <FiImage className="mx-auto h-10 w-10 mb-2" />
                    <p>Không có ảnh</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {imageSource === "upload" && (
            <div>
              {localImages && localImages.length > 0 ? (
                <div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {localImages.map((image, index) => (
                      <div
                        key={index}
                        className="relative rounded-md overflow-hidden group"
                      >
                        <img
                          src={image}
                          alt={`Ảnh ${index + 1}`}
                          className="w-full h-32 object-cover"
                        />
                        <div className="absolute top-0 right-0 p-1 flex items-center justify-center">
                          <button
                            className="bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                            onClick={() => handleDeleteImage(index)}
                          >
                            <FiX size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                    {localImages.length < 6 && (
                      <div
                        className="flex flex-col justify-center items-center h-32 bg-gray-100 rounded-md cursor-pointer hover:bg-gray-200 transition-colors border-2 border-dashed border-gray-300"
                        onClick={() =>
                          handleImageClick("upload-image-existing")
                        }
                      >
                        <FiUploadCloud className="h-8 w-8 text-gray-400" />
                        <p className="text-sm text-gray-500 mt-1">Thêm ảnh</p>
                      </div>
                    )}
                  </div>
                  <input
                    id="upload-image-existing"
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      const remainingSlots = 6 - (localImages.length || 0);

                      if (files.length > remainingSlots) {
                        toast.warning(
                          `Chỉ có thể thêm tối đa ${remainingSlots} ảnh nữa!`,
                          {
                            position: "top-right",
                            autoClose: 3000,
                          }
                        );
                      }

                      // Giới hạn số lượng file được xử lý
                      const filesToProcess = files.slice(0, remainingSlots);
                      processImageFiles(filesToProcess, localImages);

                      // Reset input để có thể chọn lại cùng một file
                      e.target.value = null;
                    }}
                  />
                </div>
              ) : (
                <div>
                  <div
                    className="flex flex-col justify-center items-center h-40 bg-gray-100 rounded-md cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => handleImageClick("upload-image-new")}
                  >
                    <div className="text-center text-gray-500">
                      <FiUploadCloud className="mx-auto h-10 w-10 mb-2" />
                      <p>Không có ảnh được tải lên</p>
                      <p className="text-sm text-blue-600 mt-2">
                        Nhấn để tải ảnh lên (tối đa 6 ảnh)
                      </p>
                    </div>
                  </div>
                  <input
                    id="upload-image-new"
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      if (files.length > 6) {
                        toast.warning("Chỉ được phép tải lên tối đa 6 ảnh!", {
                          position: "top-right",
                          autoClose: 3000,
                        });
                        return;
                      }

                      // Xử lý các file ảnh được chọn
                      processImageFiles(files, []);

                      // Reset input để có thể chọn lại cùng một file
                      e.target.value = null;
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Đăng bài */}
      {showFacebookPublishing && (
        <div className="pt-4">
          {hasFanpages ? (
            <>
              {/* Chọn fanpage */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Chọn Fanpage
                </label>
                <div
                  className="relative"
                  ref={dropdownRef}
                  data-dropdown="fanpage"
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDropdown && toggleDropdown();
                    }}
                    data-dropdown-toggle="fanpage"
                    className="w-full px-4 py-2 text-left border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex justify-between items-center"
                  >
                    <span>
                      {selectedFanpages &&
                      selectedFanpages.length > 0 &&
                      selectedFanpages[0] &&
                      selectedFanpages[0].name
                        ? `${selectedFanpages[0].name}`
                        : "Chọn Fanpage"}
                    </span>
                    <svg
                      className="h-5 w-5 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  {isDropdownOpen && (
                    <div className="absolute z-[100] w-full bg-white border border-gray-300 rounded-md mt-1 max-h-60 overflow-y-auto shadow-lg">
                      {fanpages && fanpages.length > 0 ? (
                        fanpages.map((fanpage) => (
                          <div
                            key={fanpage.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFanpageSelect &&
                                handleFanpageSelect(fanpage);
                            }}
                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between ${
                              selectedFanpages &&
                              selectedFanpages.find &&
                              selectedFanpages.find(
                                (fp) => fp.id === fanpage.id
                              )
                                ? "bg-blue-50"
                                : ""
                            }`}
                          >
                            <span className="text-left truncate">
                              {fanpage.name}
                            </span>
                            {selectedFanpages &&
                              selectedFanpages.find &&
                              selectedFanpages.find(
                                (fp) => fp.id === fanpage.id
                              ) && <span className="text-blue-600">✓</span>}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500">
                          Không có fanpage nào
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Nút đăng bài */}
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={publishPost}
                  disabled={isPublishing || selectedFanpages.length === 0}
                  className={`flex-1 px-4 py-2 text-white rounded-md font-medium text-sm flex items-center justify-center ${
                    isPublishing || selectedFanpages.length === 0
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {isPublishing ? (
                    <>
                      <FiRefreshCw className="animate-spin mr-1" />
                      Đang đăng bài...
                    </>
                  ) : (
                    "Đăng lên Fanpage Facebook"
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="mb-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-yellow-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Chưa có Fanpage kết nối
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>
                        Bạn cần kết nối Fanpage Facebook để sử dụng tính năng
                        đăng bài.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={navigateToProfile}
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-sm flex items-center justify-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L9.414 11H13a1 1 0 100-2H9.414l1.293-1.293z"
                    clipRule="evenodd"
                  />
                </svg>
                Đến trang Thiết lập Fanpage
              </button>
            </div>
          )}
        </div>
      )}

      {/* Image Preview Modal */}
      <ImagePreviewModal
        isOpen={isPreviewModalOpen}
        onClose={closePreviewModal}
        images={serperImages}
        initialIndex={previewImageIndex}
      />
    </div>
  );
};

export default GeneratedPost;
